import { useMutation, useQuery } from '@apollo/client';
import { TextareaAutosize } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import gql from 'graphql-tag';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Table from '../components/Table';
import { useMe } from '../reducers/me';

const GET_MQ_CONFIGS = gql`
  query getMqConfigs {
    mqConfigs {
      id
      user
      isPasswordPresent
      host
      port
      setup
      isKeyPresent
      cert
      ca
    }
  }
`;

const ADD_MQ_CONFIG = gql`
  mutation addMqConfig(
    $user: String
    $password: String
    $host: String!
    $port: Int!
    $setup: String!
    $key: String
    $cert: String
    $ca: String
  ) {
    addMqConfig(
      user: $user
      password: $password
      host: $host
      port: $port
      setup: $setup
      key: $key
      cert: $cert
      ca: $ca
      organization: true
    ) {
      id
      user
      isPasswordPresent
      host
      port
      setup
      isKeyPresent
      cert
      ca
    }
  }
`;

const UPDATE_MQ_CONFIG = gql`
  mutation updateMqConfig(
    $id: ID!
    $user: String
    $password: String
    $host: String!
    $port: Int!
    $setup: String!
    $key: String
    $cert: String
    $ca: String
  ) {
    updateMqConfig(
      id: $id
      user: $user
      password: $password
      host: $host
      port: $port
      setup: $setup
      key: $key
      cert: $cert
      ca: $ca
    ) {
      id
      user
      isPasswordPresent
      host
      port
      setup
      isKeyPresent
      cert
      ca
    }
  }
`;


export default function MqConfigs() {
  const { t } = useTranslation();
  const [useMeData] = useMe();
  const { loading, error, data } = useQuery(GET_MQ_CONFIGS);
  const [updateMqConfig] = useMutation(UPDATE_MQ_CONFIG);
  const [addMqConfig] = useMutation(ADD_MQ_CONFIG, {
    update: (cache, { data: { addMqConfig } }) => {
      const { mqConfigs } = cache.readQuery({ query: GET_MQ_CONFIGS });

      cache.writeQuery({
        query: GET_MQ_CONFIGS,
        data: {
          mqConfigs: mqConfigs.concat(addMqConfig),
        },
      });
    },
  });

  if (loading) {
    return <LinearProgress />;
  }

  if (error) {
    return <div>Error</div>;
  }

  const columns = [
    {
      title: t('ftpConfig:ID'),
      field: 'id',
      type: 'numeric',
      defaultSort: 'desc',
      editable: 'never',
    },
    { title: t('ftpConfig:User'), field: 'user' },
    {
      title: t('ftpConfig:Password'),
      field: 'password',
      render: (row) => row.isPasswordPresent ? '*************' : '',
    },
    { title: t('ftpConfig:Host'), field: 'host' },
    { title: t('ftpConfig:Port'), field: 'port' },
    {
      title: t('ftpConfig:Setup'), field: 'setup', lookup: {
        sonexa: 'sonexa',
      }
    },
    {
      title: t('ftpConfig:Private key'),
      field: 'key',
      render: (row) => row.isKeyPresent ? '*************' : '',
      editComponent: (props) => (
        <TextareaAutosize
          aria-label="empty textarea" placeholder="-----BEGIN PRIVATE KEY-----"
          onChange={({ target: { value } }) => props.onChange(value)}
        />
      )
    },
    {
      title: t('ftpConfig:Public certificate'),
      field: 'cert',
      render: (row) => row.isKeyPresent ? '*************' : '',
      editComponent: (props) => (
        <TextareaAutosize
          aria-label="empty textarea" placeholder="-----BEGIN CERTIFICATE-----"
          onChange={({ target: { value } }) => props.onChange(value)}
        />
      )
    },
    {
      title: t('ftpConfig:Certificate authority'),
      field: 'ca',
      render: (row) => row.isKeyPresent ? '*************' : '',
      editComponent: (props) => (
        <TextareaAutosize
          aria-label="empty textarea" placeholder="-----BEGIN CERTIFICATE-----"
          onChange={({ target: { value } }) => props.onChange(value)}
        />
      )
    },
  ];

  return (
    <React.Fragment>
      <Table
        tableId="mqConfigs"
        title=""
        columns={columns}
        data={data.mqConfigs}
        options={{
          sorting: true,
          pageSize: 10,
        }}
        editable={{
          onRowAdd: async (newData) => {
            await addMqConfig({
              variables: {
                user: newData.user,
                password: newData.password,
                host: newData.host,
                port: Number(newData.port),
                setup: newData.setup,
                key: newData.key,
                cert: newData.cert,
                ca: newData.ca,
                organizationId: useMeData.organizationId,
              },
            });
          },
          onRowUpdate: async (newData, oldData) => {
            await updateMqConfig({
              variables: {
                id: oldData.id,
                user: newData.user,
                ...(!!newData.password && { password: newData.password }),
                host: newData.host,
                port: Number(newData.port),
                setup: newData.setup,
                ...(!!newData.key && { key: newData.key }),
                ...(!!newData.cert && { cert: newData.cert }),
                ...(!!newData.ca && { ca: newData.ca }),
                organizationId: useMeData.organizationId,
              },
            });
          },
        }}
      />
    </React.Fragment>
  );
};
